// @ts-nocheck
/*
 * Port of views/dashboard/system-monitor.js (~1400 lines).
 * API: API_SYSTEM_MONITORING_STATS (single GET). Byte-identical:
 *   - SUPERADMIN only (early return)
 *   - 30-second auto-refresh + manual refresh
 *   - Cancellation refs to avoid state on unmounted
 *   - Trend history capped at 20 samples
 *   - Per-service helpers (sqlServiceRows / sqlServiceBars) preserved verbatim
 *     including engine detection (mysql / postgres / cockroachdb / unknown)
 * The DB Master / DB Slave / Asterisk / Kamailio / ClickHouse / RabbitMQ / Redis
 * service cards render exactly the same key-value rows and progress bars.
 * PM2 processes section is commented-out in the JS source — do NOT re-enable.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  HardDrive,
  MemoryStick,
  Network,
  RefreshCw,
  Server,
  Wifi,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getApiCall } from '@/service/httpCall';
import { API_SYSTEM_MONITORING_STATS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';

const REFRESH_INTERVAL_MS = 30000;

const maskValue = (value) => {
  if (!value) return '';
  const str = value.toString();
  if (str.length <= 3) return str;
  return '*'.repeat(str.length - 3) + str.slice(-3);
};

const formatUptime = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
};

const fmtMB = (mb) => {
  const n = parseFloat(mb || 0);
  if (n >= 1024) return `${(n / 1024).toFixed(2)} GB`;
  return `${n.toFixed(0)} MB`;
};

const barColor = (pct) => {
  if (pct >= 85) return '#f43f5e';
  if (pct >= 70) return '#f59e0b';
  return '#10b981';
};

const fmtNum = (n) =>
  n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString();

const fmtPct = (n) =>
  n === null || n === undefined || n === '' ? '—' : `${parseFloat(n).toFixed(2)}%`;

/* Rows shown for a SQL service depend on the engine the API detected. */
const sqlServiceRows = (svc) => {
  const engine = svc.engine;
  if (engine === 'mysql') {
    return [
      { label: 'Engine', value: svc.engineVersion || 'MySQL' },
      { label: 'Threads', value: `${svc.threadsConnected ?? '—'} conn / ${svc.threadsRunning ?? '—'} run` },
      { label: 'Uptime', value: formatUptime(svc.uptimeSeconds || 0) },
      { label: 'Received', value: fmtMB(svc.bytesReceivedMB) },
      { label: 'Sent', value: fmtMB(svc.bytesSentMB) },
      { label: 'Buffer Pool', value: fmtMB(svc.innodbBufferPoolMB) },
    ];
  }
  if (engine === 'postgres' || engine === 'cockroachdb') {
    const rows = [
      { label: 'Engine', value: svc.engineVersion || engine },
      {
        label: 'Conns',
        value: `${svc.totalConnections ?? '—'} / ${svc.maxConnections ?? '—'} (${svc.activeConnections ?? '—'} active)`,
      },
      { label: 'DB Size', value: fmtMB(svc.dbSizeMB) },
    ];
    if (svc.uptimeSeconds !== undefined) rows.push({ label: 'Uptime', value: formatUptime(svc.uptimeSeconds || 0) });
    if (svc.cacheHitPercent !== undefined) rows.push({ label: 'Cache Hit', value: fmtPct(svc.cacheHitPercent) });
    if (svc.sharedBuffersMB !== undefined) rows.push({ label: 'Shared Buffers', value: fmtMB(svc.sharedBuffersMB) });
    if (svc.deadlocks !== undefined) rows.push({ label: 'Deadlocks', value: fmtNum(svc.deadlocks) });
    return rows;
  }
  return [
    { label: 'Engine', value: svc.engineVersion || 'unknown' },
    { label: 'Error', value: svc.statsError || svc.error || '—' },
  ];
};

const sqlServiceBars = (svc) => {
  const pool = svc.pool || {};
  const disk = svc.disk || {};
  const bars = [
    {
      label: 'Pool',
      pct: pool.max ? (pool.used / pool.max) * 100 : 0,
      meta: `${pool.used ?? '—'} / ${pool.max ?? '—'}`,
    },
  ];
  const diskTotal = parseFloat(disk.totalGB);
  if (diskTotal) {
    bars.push({
      label: 'Disk',
      pct: (parseFloat(disk.usedGB) / diskTotal) * 100,
      meta: `${disk.usedGB} / ${disk.totalGB} GB`,
    });
  } else if (svc.maxConnections && svc.totalConnections !== undefined) {
    bars.push({
      label: 'Conns',
      pct: (svc.totalConnections / svc.maxConnections) * 100,
      meta: `${svc.totalConnections} / ${svc.maxConnections}`,
    });
  }
  if (svc.cacheHitPercent !== undefined && svc.cacheHitPercent !== null) {
    const pct = parseFloat(svc.cacheHitPercent);
    bars.push({
      label: 'Cache Hit',
      pct,
      meta: `${pct.toFixed(1)}%`,
      color: pct >= 95 ? '#10b981' : pct >= 85 ? '#f59e0b' : '#f43f5e',
    });
  }
  return bars;
};

function StatCard({ icon, label, value, unit, sub, progress, statusIcon, statusLabel, accentColor }) {
  return (
    <div className="flex h-full flex-col gap-2.5 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-1 text-xs font-semibold text-muted-foreground">{label}</div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-extrabold leading-none text-foreground">{value}</span>
            {unit && <span className="text-sm font-medium text-muted-foreground">{unit}</span>}
          </div>
          {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
        </div>
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
          style={{
            background: accentColor ? `${accentColor}18` : 'hsl(var(--primary) / 0.1)',
            color: accentColor || 'hsl(var(--primary))',
          }}
        >
          {icon}
        </div>
      </div>
      {progress !== undefined && (
        <div className="h-1.5 overflow-hidden rounded-full bg-border">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%`, background: barColor(progress) }}
          />
        </div>
      )}
      {(statusIcon !== undefined || statusLabel) && (
        <div className="flex items-center gap-1.5">
          {statusIcon}
          <span className="text-[11px] font-semibold">{statusLabel}</span>
        </div>
      )}
    </div>
  );
}

function ServiceCard({ name, service, bars = [], rows = [] }) {
  const isConnected = service?.status === 'connected';
  return (
    <div className="flex flex-col gap-2 shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-bold text-foreground">{name}</span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: isConnected ? '#10b981' : '#f43f5e' }}
          />
          <span
            className="text-[10px] font-bold uppercase tracking-wider"
            style={{ color: isConnected ? '#10b981' : '#f43f5e' }}
          >
            {service?.status || 'unknown'}
          </span>
        </span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        {service?.latencyMs !== undefined && (
          <div>
            <span className="font-bold text-foreground">{service.latencyMs}</span> ms
          </div>
        )}
        {service?.host && (
          <div className="text-[10px]">{maskValue(service.host)}</div>
        )}
      </div>
      {bars.length > 0 && (
        <div className="mt-0.5 flex flex-col gap-2">
          {bars.map(({ label, pct, meta, color }) => {
            const clampedPct = Math.min(Math.max(pct || 0, 0), 100);
            const c = color || barColor(clampedPct);
            return (
              <div key={label}>
                <div className="mb-1 flex justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </span>
                  <span className="text-[10px] font-bold" style={{ color: c }}>
                    {meta}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${clampedPct}%`, background: c }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {rows.length > 0 && (
        <div className="mt-0.5 flex flex-col gap-1 rounded-md bg-muted/50 p-2">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </span>
              <span className="text-[11px] font-bold text-foreground">{value ?? '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SystemMonitor() {
  const [systemData, setSystemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [history, setHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const cancelledRef = useRef(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const checkUserRole = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {
        console.error('Invalid user data', e);
      }
    };
    checkUserRole();
  }, []);

  const fetchSystemStats = useCallback(async ({ manual = false } = {}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (manual) setRefreshing(true);
    try {
      const resp = await getApiCall(API_SYSTEM_MONITORING_STATS, {});
      if (cancelledRef.current) return;
      setSystemData(resp);
      setLastUpdatedAt(new Date());
      setHistory((prev) => [
        ...prev.slice(-19),
        {
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          cpu: parseFloat(resp.system?.cpuLoadPercent || 0),
          memory: parseFloat(resp.system?.usagePercent || 0),
        },
      ]);
    } catch (err) {
      if (cancelledRef.current) return;
      toast.error('Failed to fetch system stats', { id: 'system-monitor-stats-error' });
      console.error(err);
    } finally {
      inFlightRef.current = false;
      if (!cancelledRef.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!loggedInUserRole || loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    cancelledRef.current = false;
    fetchSystemStats();
    const timer = setInterval(() => {
      if (!document.hidden) fetchSystemStats(); // skip in background tabs
    }, REFRESH_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(timer);
    };
  }, [loggedInUserRole, fetchSystemStats]);

  if (loading || !systemData) {
    return (
      <Card className="mb-6 p-6 text-center text-sm text-muted-foreground">
        Loading system monitor…
      </Card>
    );
  }

  const sys = systemData.system || {};
  const disk = systemData.disk || {};
  const services = systemData.services || {};
  const network = sys.network || { downloadMbps: '0.00', uploadMbps: '0.00' };
  const connections = sys.connections || { established: 0, http: 0, websocket: 0, total: 0 };

  const memoryUsedPercent = parseFloat(sys.usagePercent || 0);
  const cpuUsedPercent = parseFloat(sys.cpuLoadPercent || 0);
  const diskUsedPercent = parseFloat(disk.usagePercent || 0);

  const getStatusIcon = (percent) =>
    percent > 80 ? (
      <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
    ) : (
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
    );

  const getStatusLabel = (percent) => {
    if (percent > 80) return 'CRITICAL';
    if (percent > 70) return 'HIGH';
    return 'NORMAL';
  };

  return (
    <div className="mb-6 mt-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h5 className="m-0 flex items-center gap-2 text-lg font-extrabold text-foreground">
          <Server className="h-5 w-5 text-primary" />
          System Monitor
        </h5>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: refreshing ? '#f59e0b' : '#10b981' }}
            />
            {refreshing
              ? 'Refreshing…'
              : lastUpdatedAt
                ? `Live • every 30s • updated ${lastUpdatedAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}`
                : 'Not updated yet'}
          </div>
          <button
            type="button"
            onClick={() => fetchSystemStats({ manual: true })}
            disabled={refreshing}
            title="Re-read system stats"
            className={
              'flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-bold text-foreground ' +
              (refreshing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted')
            }
          >
            <RefreshCw className={'h-3 w-3 ' + (refreshing ? 'animate-spin' : '')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Row 1: Stat cards */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Cpu className="h-5 w-5" />}
          label="CPU Usage"
          value={cpuUsedPercent.toFixed(1)}
          unit="%"
          sub={`${sys.cpuCores || '—'} cores`}
          progress={cpuUsedPercent}
          statusIcon={getStatusIcon(cpuUsedPercent)}
          statusLabel={getStatusLabel(cpuUsedPercent)}
          accentColor="#0ea5e9"
        />
        <StatCard
          icon={<MemoryStick className="h-5 w-5" />}
          label="Memory Usage"
          value={memoryUsedPercent.toFixed(1)}
          unit="%"
          sub={`${parseFloat(sys.usedMemInMB || 0).toFixed(0)} / ${parseFloat(sys.totalMemInMB || 0).toFixed(0)} MB`}
          progress={memoryUsedPercent}
          statusIcon={getStatusIcon(memoryUsedPercent)}
          statusLabel={memoryUsedPercent > 80 ? 'CRITICAL — LOW MEMORY' : 'OK'}
          accentColor="#f59e0b"
        />
        <StatCard
          icon={<HardDrive className="h-5 w-5" />}
          label="Disk Usage"
          value={diskUsedPercent.toFixed(1)}
          unit="%"
          sub={`${disk.usedGB || '—'} / ${disk.totalGB || '—'} GB`}
          progress={diskUsedPercent}
          statusIcon={getStatusIcon(diskUsedPercent)}
          statusLabel={diskUsedPercent > 80 ? 'CRITICAL — LOW SPACE' : 'OK'}
          accentColor="#10b981"
        />
        {/* Network card */}
        <div className="flex h-full flex-col gap-2.5 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="flex items-start justify-between">
            <div className="text-xs font-semibold text-muted-foreground">Network Throughput</div>
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
              style={{ background: '#6366f118', color: '#6366f1' }}
            >
              <Network className="h-5 w-5" />
            </div>
          </div>
          <div className="-mt-4 flex flex-col gap-1">
            <div className="flex items-baseline gap-1">
              <span className="text-[13px] font-bold" style={{ color: '#10b981' }}>↓</span>
              <span className="text-2xl font-extrabold text-foreground">
                {parseFloat(network.downloadMbps).toFixed(1)}
              </span>
              <span className="text-xs text-muted-foreground">Mbps</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-[13px] font-bold" style={{ color: '#0ea5e9' }}>↑</span>
              <span className="text-2xl font-extrabold text-foreground">
                {parseFloat(network.uploadMbps).toFixed(1)}
              </span>
              <span className="text-xs text-muted-foreground">Mbps</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Wifi className="h-3 w-3 text-primary" />
            <span className="text-[11px] font-medium text-muted-foreground">
              {connections.total} active connections
            </span>
          </div>
        </div>
      </div>

      {/* Row 2: Uptime + Connections + CPU cores */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            System Uptime
          </div>
          <div className="mb-1 text-3xl font-extrabold text-foreground">
            {formatUptime(sys.uptimeInSeconds || 0)}
          </div>
          <div className="text-xs text-muted-foreground">
            {parseFloat(sys.uptimeInSeconds || 0).toFixed(0)} seconds total
          </div>
        </div>

        <div className="shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Active Connections
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2.5">
            {[
              { label: 'Total', value: connections.total, color: '#6366f1' },
              { label: 'Established', value: connections.established, color: '#10b981' },
              { label: 'HTTP', value: connections.http, color: '#f97316' },
              { label: 'WebSocket', value: connections.websocket, color: '#0ea5e9' },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="rounded-md border border-border bg-muted/30 px-3 py-2"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {label}
                </div>
                <div className="text-2xl font-extrabold" style={{ color }}>
                  {value ?? '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            CPU Cores
          </div>
          <div className="text-5xl font-black leading-none text-primary">
            {sys.cpuCores || '—'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Logical processors</div>
          <div className="mt-3 flex flex-wrap gap-1">
            {Array.from({ length: sys.cpuCores || 0 }).map((_, i) => (
              <div
                key={i}
                className="h-[18px] w-[18px] rounded"
                style={{
                  background: barColor(cpuUsedPercent),
                  opacity: 0.7,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Trend line chart */}
      <div className="mb-3 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
        <div className="mb-3 text-[15px] font-bold text-foreground">
          Resource Usage Trend
          <span className="ml-2 text-xs font-medium text-muted-foreground">
            Last 20 samples
          </span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={history} margin={{ top: 4, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 10,
                color: 'hsl(var(--foreground))',
                fontSize: 12,
              }}
            />
            <Line type="natural" dataKey="cpu" stroke="#0ea5e9" strokeWidth={2.5} dot={false} name="CPU %" />
            <Line
              type="natural"
              dataKey="memory"
              stroke="#f59e0b"
              strokeWidth={2.5}
              dot={false}
              name="Memory %"
            />
            <Legend
              verticalAlign="bottom"
              height={28}
              iconType="circle"
              iconSize={9}
              wrapperStyle={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Row 5: Services grid */}
      <div className="mb-6 mt-6">
        <div className="mb-3 flex items-center gap-2 text-[15px] font-bold text-foreground">
          <Server className="h-4 w-4 text-primary" />
          Service Health
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {/* DB Master */}
          {(() => {
            const db = services.dbMaster || {};
            const rows = sqlServiceRows(db);
            if (db.memRssMB !== undefined) rows.push({ label: 'Mem RSS', value: fmtMB(db.memRssMB) });
            if (db.cpuUserPercent !== undefined) rows.push({ label: 'CPU User', value: fmtPct(db.cpuUserPercent) });
            if (db.cpuSysPercent !== undefined) rows.push({ label: 'CPU Sys', value: fmtPct(db.cpuSysPercent) });
            return (
              <ServiceCard name="DB Master" service={services.dbMaster} bars={sqlServiceBars(db)} rows={rows} />
            );
          })()}

          {/* DB Slave */}
          {(() => {
            const db = services.dbSlave || {};
            const rows = sqlServiceRows(db);
            if (db.memRssMB !== undefined) rows.push({ label: 'Mem RSS', value: fmtMB(db.memRssMB) });
            if (db.cpuUserPercent !== undefined) rows.push({ label: 'CPU User', value: fmtPct(db.cpuUserPercent) });
            if (db.cpuSysPercent !== undefined) rows.push({ label: 'CPU Sys', value: fmtPct(db.cpuSysPercent) });
            if (db.sameHostAsMaster) rows.push({ label: 'Replica', value: 'same node as master' });
            else if (db.isStandby) {
              rows.push({
                label: 'Repl Lag',
                value:
                  db.replicationLagSeconds === null || db.replicationLagSeconds === undefined
                    ? '—'
                    : `${db.replicationLagSeconds}s`,
              });
            }
            return (
              <ServiceCard name="DB Slave" service={services.dbSlave} bars={sqlServiceBars(db)} rows={rows} />
            );
          })()}

          {/* Asterisk */}
          {(() => {
            const svc = services.asterisk || {};
            return (
              <ServiceCard name="Asterisk" service={services.asterisk} bars={[]} rows={sqlServiceRows(svc)} />
            );
          })()}

          {/* Kamailio */}
          {(() => {
            const svc = services.kamailio || {};
            return (
              <ServiceCard name="Kamailio" service={services.kamailio} bars={[]} rows={sqlServiceRows(svc)} />
            );
          })()}

          {/* ClickHouse */}
          {(() => {
            const svc = services.clickhouse || {};
            const load = svc.load || {};
            const ram = svc.ram || {};
            const chDisk = svc.disk || {};
            const diskPct = parseFloat(chDisk.usagePercent || 0);
            return (
              <ServiceCard
                name="ClickHouse"
                service={services.clickhouse}
                bars={[
                  {
                    label: 'Disk',
                    pct: diskPct,
                    meta: `${chDisk.usedGB ?? '—'} / ${chDisk.totalGB ?? '—'} GB`,
                  },
                ]}
                rows={[
                  { label: 'Active Queries', value: load.activeQueries ?? '—' },
                  { label: 'TCP Conns', value: load.tcpConnections ?? '—' },
                  { label: 'HTTP Conns', value: load.httpConnections ?? '—' },
                  { label: 'Mem Resident', value: fmtMB(ram.memoryResidentMB) },
                  { label: 'Mem Tracking', value: fmtMB(ram.memoryTrackingMB) },
                ]}
              />
            );
          })()}

          {/* RabbitMQ */}
          {(() => {
            const svc = services.rabbitmq || {};
            const msgs = svc.messages || {};
            const rates = svc.messageRates || {};
            const node = svc.node || {};
            const nRam = node.ram || {};
            const ramPct = parseFloat(nRam.limitMB)
              ? (parseFloat(nRam.usedMB) / parseFloat(nRam.limitMB)) * 100
              : 0;
            return (
              <ServiceCard
                name="RabbitMQ"
                service={services.rabbitmq}
                bars={[
                  {
                    label: 'Node RAM',
                    pct: ramPct,
                    meta: `${fmtMB(nRam.usedMB)} / ${fmtMB(nRam.limitMB)}`,
                  },
                ]}
                rows={[
                  { label: 'Version', value: svc.version ? `v${svc.version}` : '—' },
                  ...(svc.nodesTotal > 1
                    ? [
                        { label: 'Nodes Up', value: `${svc.nodesRunning ?? '—'} / ${svc.nodesTotal}` },
                        { label: 'Sampled', value: (svc.nodeName || '—').replace(/^rabbit@/, '') },
                      ]
                    : []),
                  ...(svc.anyMemoryAlarm || svc.anyDiskAlarm
                    ? [
                        {
                          label: 'Alarm',
                          value: [svc.anyMemoryAlarm && 'memory', svc.anyDiskAlarm && 'disk']
                            .filter(Boolean)
                            .join(' + '),
                        },
                      ]
                    : []),
                  { label: 'Uptime', value: formatUptime(node.uptimeSeconds || 0) },
                  { label: 'Connections', value: fmtNum(svc.connections) },
                  { label: 'Queues', value: fmtNum(svc.queues) },
                  { label: 'Consumers', value: fmtNum(svc.consumers) },
                  { label: 'Channels', value: fmtNum(svc.channels) },
                  { label: 'Msgs Total', value: fmtNum(msgs.total) },
                  { label: 'Ready', value: fmtNum(msgs.ready) },
                  { label: 'Unacked', value: fmtNum(msgs.unacked) },
                  { label: 'Pub/s', value: rates.publishPerSec ?? '—' },
                  { label: 'Del/s', value: rates.deliverPerSec ?? '—' },
                  { label: 'Ack/s', value: rates.ackPerSec ?? '—' },
                ]}
              />
            );
          })()}

          {/* Redis */}
          {(() => {
            const svc = services.redis || {};
            const load = svc.load || {};
            const ram = svc.ram || {};
            const hits = load.keyspaceHits || 0;
            const misses = load.keyspaceMisses || 0;
            const total = hits + misses;
            const hitPct = total > 0 ? (hits / total) * 100 : 0;
            const limitMB = parseFloat(ram.limitMB);
            const memPct =
              ram.usagePercent != null
                ? parseFloat(ram.usagePercent)
                : limitMB
                  ? (parseFloat(ram.usedMemoryMB) / limitMB) * 100
                  : 0;
            const limitLabel =
              ram.limitSource === 'maxmemory'
                ? 'maxmemory'
                : ram.limitSource === 'system'
                  ? 'system RAM'
                  : 'peak';
            const memMeta = limitMB
              ? `${fmtMB(ram.usedMemoryMB)} / ${fmtMB(ram.limitMB)} ${limitLabel}`
              : `${fmtMB(ram.usedMemoryMB)} / ${fmtMB(ram.peakMemoryMB)} peak`;
            return (
              <ServiceCard
                name="Redis"
                service={services.redis}
                bars={[
                  { label: 'Memory', pct: memPct, meta: memMeta },
                  {
                    label: 'Hit Rate',
                    pct: hitPct,
                    meta: `${hitPct.toFixed(1)}%`,
                    color: hitPct >= 60 ? '#10b981' : hitPct >= 40 ? '#f59e0b' : '#f43f5e',
                  },
                ]}
                rows={[
                  { label: 'Version', value: svc.version ? `v${svc.version}` : '—' },
                  {
                    label: 'Role',
                    value:
                      svc.mode === 'sentinel'
                        ? `${svc.role || '?'} (sentinel)`
                        : svc.role || '—',
                  },
                  ...(svc.connectedReplicas > 0
                    ? [
                        {
                          label: 'Replicas',
                          value: `${svc.connectedReplicas} (${(svc.replicas || [])
                            .map((r) => `${r.state ?? '?'} +${r.lagSeconds ?? '?'}s`)
                            .join(', ')})`,
                        },
                      ]
                    : []),
                  ...(svc.role === 'slave'
                    ? [{ label: 'Master Link', value: svc.masterLinkStatus || '—' }]
                    : []),
                  { label: 'Uptime', value: formatUptime(svc.uptimeSeconds || 0) },
                  { label: 'Clients', value: fmtNum(load.connectedClients) },
                  { label: 'Ops/s', value: fmtNum(load.opsPerSec) },
                  { label: 'Keys', value: fmtNum(svc.dbKeys) },
                  { label: 'Peak Mem', value: fmtMB(ram.peakMemoryMB) },
                  { label: 'Evicted', value: fmtNum(load.evictedKeys) },
                  { label: 'Hits', value: fmtNum(hits) },
                  { label: 'Misses', value: fmtNum(misses) },
                ]}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
}
